import { useCallback, useEffect, useRef, useState } from "react";
import { uploadUrl } from "../../lib/assets";
import { youtubeEmbedUrl } from "../../lib/youtube";
import {
  formatVideoTime,
  getSavedVideoTime,
  saveVideoTime,
} from "../../lib/videoStorage";

function PlayIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

/**
 * @param {{ film: { id: string, youtubeVideoId?: string, cat: string, title: string, img?: string, thumbnailUrl?: string, date?: string, dur?: string, secs?: number, dek?: string }, featured?: boolean, index: number, autoPlay?: boolean }} props
 */
export default function FilmPlayer({ film, featured = false, index, autoPlay = false }) {
  const stageRef = useRef(null);
  const rafRef = useRef(0);
  const lastTickRef = useRef(0);
  const storageKey = film.youtubeVideoId || film.id;
  const timeRef = useRef(getSavedVideoTime(storageKey));

  const [playing, setPlaying] = useState(autoPlay && Boolean(film.youtubeVideoId));
  const [currentTime, setCurrentTime] = useState(timeRef.current);

  const usesYoutube = Boolean(film.youtubeVideoId);
  const posterUrl = film.thumbnailUrl || (film.img ? uploadUrl(film.img) : "");
  const progress = film.secs ? Math.min(100, (currentTime / film.secs) * 100) : 0;

  const draw = useCallback((time) => {
    timeRef.current = time;
    setCurrentTime(time);
  }, []);

  const setPlayingState = useCallback(
    (on) => {
      setPlaying(on);
      if (on) {
        lastTickRef.current = performance.now();
      } else if (!usesYoutube) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
        }
        saveVideoTime(storageKey, timeRef.current);
      }
    },
    [storageKey, usesYoutube]
  );

  useEffect(() => {
    if (usesYoutube || !playing) {
      return undefined;
    }

    const tick = (now) => {
      const elapsed = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      let next = timeRef.current + elapsed;

      if (next >= film.secs) {
        draw(0);
        saveVideoTime(storageKey, 0);
        setPlaying(false);
        return;
      }

      draw(next);
      if (Math.floor(next) !== Math.floor(next - elapsed)) {
        saveVideoTime(storageKey, next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, storageKey, film.secs, draw, usesYoutube]);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  useEffect(() => {
    if (autoPlay && film.youtubeVideoId) {
      setPlaying(true);
    }
  }, [autoPlay, film.youtubeVideoId]);

  const handleScrub = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const next = ratio * film.secs;
    timeRef.current = next;
    lastTickRef.current = performance.now();
    draw(next);
    saveVideoTime(storageKey, next);
  };

  const handleFullscreen = () => {
    const stage = stageRef.current;
    if (stage?.requestFullscreen) {
      stage.requestFullscreen().catch(() => {});
    }
  };

  const savedStart = Math.floor(getSavedVideoTime(storageKey));
  const embedSrc = usesYoutube
    ? `${youtubeEmbedUrl(film.youtubeVideoId)}?autoplay=1&rel=0&modestbranding=1${
        savedStart > 0 ? `&start=${savedStart}` : ""
      }`
    : "";

  return (
    <article
      className="film-player"
      style={{ animationDelay: `${index * 0.08}s` }}
      data-vid={film.id}
    >
      <div
        ref={stageRef}
        className={`film-player__stage${featured ? " film-player__stage--featured" : ""}`}
      >
        {usesYoutube && playing ? (
          <iframe
            className="film-player__embed"
            src={embedSrc}
            title={film.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <>
            <div
              className={`film-player__poster${playing ? " is-dimmed" : ""}`}
              style={posterUrl ? { backgroundImage: `url('${posterUrl}')` } : undefined}
            />
            <div className={`film-player__shade${playing ? " is-hidden" : ""}`} />

            <button
              type="button"
              className={`film-player__play${playing ? " is-hidden" : ""}`}
              aria-label="Play"
              onClick={() => setPlayingState(true)}
            >
              <PlayIcon />
            </button>

            <div className={`film-player__meta${playing ? " is-hidden" : ""}`}>
              <div className="film-player__meta-inner">
                <div className="film-player__meta-row">
                  <span>{film.cat.toUpperCase()}</span>
                  {(film.date || film.dur) && <span className="film-player__meta-rule" />}
                  <span>
                    {[film.date, film.dur].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <h2 className={`film-player__title${featured ? " film-player__title--featured" : ""}`}>
                  {film.title}
                </h2>
                {film.dek ? <p className="film-player__dek">{film.dek}</p> : null}
              </div>
            </div>

            {!usesYoutube && (
              <div className={`film-player__controls${playing ? " is-visible" : ""}`}>
                <div className="film-player__controls-inner">
                  <button
                    type="button"
                    className="film-player__scrub"
                    aria-label="Seek"
                    onClick={handleScrub}
                  >
                    <div className="film-player__scrub-fill" style={{ width: `${progress}%` }} />
                    <div className="film-player__scrub-knob" style={{ left: `${progress}%` }} />
                  </button>

                  <div className="film-player__toolbar">
                    <button
                      type="button"
                      className="film-player__toggle"
                      aria-label={playing ? "Pause" : "Play"}
                      onClick={() => setPlayingState(!playing)}
                    >
                      {playing ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <span className="film-player__time">
                      {formatVideoTime(currentTime)} / {film.dur}
                    </span>
                    <span className="film-player__toolbar-spacer" />
                    <span className="film-player__toolbar-cat">{film.cat.toUpperCase()}</span>
                    <button
                      type="button"
                      className="film-player__fullscreen"
                      aria-label="Fullscreen"
                      onClick={handleFullscreen}
                    >
                      <FullscreenIcon />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}
